import { z } from "zod";
import { FeedbackType, FeedbackDeviceType } from "../lib/enums";

// ----- Input schema -----

export const SubmitFeedbackInputSchema = z
  .object({
    type: z.nativeEnum(FeedbackType),
    // Anonymous submitters only — logged-in members' name/email are resolved
    // server-side from ctx.session.contactId, and these are ignored then.
    name: z.string().optional(),
    email: z.string().optional(),
    // Bug fields
    bug_url: z.string().optional(),
    bug_steps: z.string().optional(),
    bug_expected: z.string().optional(),
    bug_device_type: z.nativeEnum(FeedbackDeviceType).optional(),
    bug_browser: z.string().optional(),
    bug_os: z.string().optional(),
    bug_other_info: z.string().optional(),
    // Feature request fields
    feature_description: z.string().optional(),
    feature_page: z.string().optional(),
    // Comment field
    comment_message: z.string().optional(),
    recaptcha_token: z.string().min(1, "Please complete the reCAPTCHA"),
  })
  .superRefine((data, ctx) => {
    if (data.type === FeedbackType.Bug) {
      if (!data.bug_url?.trim())
        ctx.addIssue({ code: "custom", path: ["bug_url"], message: "This field is required" });
      if (!data.bug_steps?.trim())
        ctx.addIssue({ code: "custom", path: ["bug_steps"], message: "This field is required" });
      if (!data.bug_expected?.trim())
        ctx.addIssue({
          code: "custom",
          path: ["bug_expected"],
          message: "This field is required",
        });
      if (!data.bug_device_type)
        ctx.addIssue({
          code: "custom",
          path: ["bug_device_type"],
          message: "Please select a device type",
        });
      if (!data.bug_browser?.trim())
        ctx.addIssue({
          code: "custom",
          path: ["bug_browser"],
          message: "This field is required",
        });
      if (!data.bug_os?.trim())
        ctx.addIssue({ code: "custom", path: ["bug_os"], message: "This field is required" });
    }
    if (data.type === FeedbackType.FeatureRequest) {
      if (!data.feature_description?.trim())
        ctx.addIssue({
          code: "custom",
          path: ["feature_description"],
          message: "This field is required",
        });
      if (!data.feature_page?.trim())
        ctx.addIssue({
          code: "custom",
          path: ["feature_page"],
          message: "This field is required",
        });
    }
    if (data.type === FeedbackType.Comment) {
      if (!data.comment_message?.trim())
        ctx.addIssue({
          code: "custom",
          path: ["comment_message"],
          message: "This field is required",
        });
    }
  });

export type SubmitFeedbackInput = z.infer<typeof SubmitFeedbackInputSchema>;

// ----- Output schema -----

const FeedbackSubmissionCreatedSchema = z.object({ id: z.string(), created: z.literal(true) });
export const SubmitFeedbackOutputSchema = FeedbackSubmissionCreatedSchema;
export type SubmitFeedbackOutput = z.infer<typeof SubmitFeedbackOutputSchema>;
